import { useState, useEffect, useRef, type FormEvent } from 'react';
import { X, Spline, Minus, Redo, MoveRight } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useNotification } from '@/hooks/useNotification';
import { Button } from '@/components/ui/Button';
import { addTask, addAudioTask, addVideoTask } from '../../../apiServices/taskApi';
import { putToStorageTaskSnapShot } from '../../../apiServices/storageApiService';
import { addTransformedTask, replaceTask, removeTask } from '../../../app/features/project/projectSlice';
import { formatMilliSeconds } from '../../../util/UtilFunctions';
import { lineTypes, type LineType, isLineType } from '../lib/lineMath';
import { TaskDot } from './TaskDot';
import { ArrowOverlay } from './ArrowOverlay';

interface NewTaskFormProps {
  mediaType: 'video' | 'audio' | 'image';
  resource: { id: string; parent: string; type: string; [k: string]: unknown };
  taskIndex: number;
  onClose: () => void;
  /** Video/image: percentage coords of click point */
  coordX?: number;
  coordY?: number;
  /** Video: timestamp in ms */
  time?: number;
  /** Image: natural pixel coords for storage */
  naturalCoord?: { left: number; top: number };
  /** Drawn line pixel coords */
  pixelCoords?: [number, number][] | null;
  /** Initial line type from drawing */
  initialTaskType?: LineType;
  /** Video ref for snapshot capture */
  videoRef?: React.RefObject<HTMLVideoElement | null>;
  /** Callback to adjust video playback during duration editing */
  onSeek?: (seconds: number) => void;
  /** Container dimensions for rendering line preview */
  containerWidth?: number;
  containerHeight?: number;
}

const LINE_TYPE_OPTIONS: { value: LineType; icon: typeof Spline; label: string }[] = [
  { value: lineTypes.freeLine, icon: Spline, label: 'Free line' },
  { value: lineTypes.straightLine, icon: Minus, label: 'Straight line' },
  { value: lineTypes.freeArrow, icon: Redo, label: 'Free arrow' },
  { value: lineTypes.straightArrow, icon: MoveRight, label: 'Straight arrow' },
];

/** Capture a video frame as a Blob for thumbnail upload */
function captureVideoSnapshot(videoEl: HTMLVideoElement): Blob | null {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = videoEl.videoWidth;
    canvas.height = videoEl.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg');
    const binary = atob(dataUrl.split(',')[1]);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: 'image/jpeg' });
  } catch {
    return null;
  }
}

/** Position the form near the click point */
function getFormPosition(coordX: number, coordY: number, mediaType: string) {
  if (mediaType === 'audio') {
    return { top: 0, left: 0, bottom: 0, right: 0, margin: 'auto', height: 'fit-content' } as const;
  }

  let offsetTop: number;
  let offsetLeft: number;
  if (coordY > 66) offsetTop = 10;
  else if (coordY < 33) offsetTop = 2;
  else offsetTop = 5;
  if (coordX > 50) offsetLeft = 27;
  else offsetLeft = -2;

  return {
    top: `calc(${coordY}% - ${offsetTop}rem)`,
    left: `calc(${coordX}% - ${offsetLeft}rem)`,
  };
}

export function NewTaskForm({
  mediaType,
  resource,
  taskIndex,
  onClose,
  coordX = 50,
  coordY = 50,
  time,
  naturalCoord,
  pixelCoords,
  initialTaskType,
  videoRef,
  onSeek,
  containerWidth,
  containerHeight,
}: NewTaskFormProps) {
  const dispatch = useAppDispatch();
  const { notify } = useNotification();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const uid = useAppSelector((state) => state.user.uid) as string | null;
  const projectUsers = useAppSelector((state) => state.project.users);

  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [duration, setDuration] = useState(0.5);
  const [taskType, setTaskType] = useState<string>(initialTaskType ?? 'default');

  const isArrowType = isLineType(taskType);
  const showLineOptions = mediaType !== 'audio' && isArrowType;
  const showDuration = mediaType === 'video' && isArrowType;

  // Close on mousedown outside — ignore clicks within 200ms of mount to avoid
  // the triggering mouseup from immediately closing the form
  const mountedAt = useRef(Date.now());
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (Date.now() - mountedAt.current < 200) return;
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside, false);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, false);
    };
  }, [onClose]);

  const handleDurationChange = (val: number) => {
    if (onSeek && time != null) {
      const diff = val - duration;
      const currentTime = (time / 1000) + val;
      onSeek(currentTime);
    }
    setDuration(val);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    setIsSubmitting(true);

    // Build optimistic task shown immediately — with the real owner/userData so
    // it doesn't flash as "[Removed user]" until the server responds
    const tempId = `temp-${Date.now()}`;
    const ownUserData = projectUsers.find((u) => u.uid === uid);
    // Image tasks store NATURAL-PIXEL coords (matching the server + coordTransform);
    // video tasks store percentages. Using percent for image made the optimistic
    // dot flash at the top-left corner until the server response replaced it.
    const optimisticTask = {
      id: tempId,
      resource: resource.id,
      type: resource.type,
      index: taskIndex,
      coord_x: mediaType === 'image' ? naturalCoord?.left : mediaType === 'video' ? coordX : undefined,
      coord_y: mediaType === 'image' ? naturalCoord?.top : mediaType === 'video' ? coordY : undefined,
      coordX,
      coordY,
      timestamp: time,
      duration: mediaType === 'video' ? duration : undefined,
      taskType: taskType || 'default',
      coords: pixelCoords || false,
      owner: uid ?? '',
      created: new Date().toString(),
      message,
      messages: [{ content: message, created: new Date().toString(), sender: uid ?? '', userData: ownUserData }],
      userData: ownUserData,
      status: 'open',
    };
    dispatch(addTransformedTask(optimisticTask));
    onClose();

    try {
      if (mediaType === 'audio') {
        const res = await addAudioTask(
          resource.parent, resource.type, taskIndex, resource.id, message, time!,
        );
        dispatch(replaceTask({ tempId, rawTask: res.response.Item }));
      } else if (mediaType === 'image') {
        let coords = pixelCoords || null;
        if (coords && (taskType === lineTypes.straightLine || taskType === lineTypes.straightArrow)) {
          coords = [coords[0], coords[coords.length - 1]];
        }
        const res = await addTask({
          parent: resource.parent,
          type: resource.type,
          index: taskIndex,
          resource: resource.id,
          message,
          coord_x: naturalCoord?.left ?? 0,
          coord_y: naturalCoord?.top ?? 0,
          taskType: taskType || 'default',
          coords,
        });
        dispatch(replaceTask({ tempId, rawTask: res.response.Item }));
      } else if (mediaType === 'video') {
        // Capture snapshot — isolated so a failure doesn't block task creation
        let snapshotKey: string | undefined;
        let snapshotFailed = false;
        if (videoRef?.current) {
          const blob = captureVideoSnapshot(videoRef.current);
          if (blob) {
            try {
              const storageRes = await putToStorageTaskSnapShot(blob);
              snapshotKey = storageRes.key;
            } catch {
              snapshotFailed = true;
            }
          }
        }

        let coords = pixelCoords || null;
        if (coords && (taskType === lineTypes.straightLine || taskType === lineTypes.straightArrow)) {
          coords = [coords[0], coords[coords.length - 1]];
        }

        const res = await addVideoTask({
          parent: resource.parent,
          type: resource.type,
          index: taskIndex,
          resource: resource.id,
          message,
          coordX,
          coordY,
          timestamp: time!,
          snapshotThumbnailStorage: snapshotKey,
          taskType: taskType || 'default',
          duration,
          coords,
        });
        dispatch(replaceTask({ tempId, rawTask: res.response.Item }));

        if (snapshotFailed) {
          notify({ id: String(Date.now()), type: 'warning', message: 'Task created, but thumbnail could not be saved' });
        }
      }
    } catch {
      dispatch(removeTask(tempId));
      notify({ id: String(Date.now()), type: 'error', message: 'Could not add task' });
    }
  };

  const positionStyle = getFormPosition(coordX, coordY, mediaType);

  return (
    <div className="absolute inset-0 z-[100]" ref={wrapperRef}>
      {/* Dot at click point (video/image) */}
      {mediaType !== 'audio' && (
        <TaskDot
          coordX={coordX}
          coordY={coordY}
          taskType={initialTaskType}
          onClick={onClose}
        />
      )}

      {/* Line preview while form is open */}
      {pixelCoords && pixelCoords.length > 1 && containerWidth && containerHeight && (
        <ArrowOverlay
          coords={pixelCoords}
          type={(taskType as LineType) || lineTypes.freeLine}
          viewBoxWidth={containerWidth}
          viewBoxHeight={containerHeight}
        />
      )}

      {/* Form modal */}
      <div
        className="absolute z-10 w-[25rem] max-w-[95%] flex flex-col rounded-xl bg-card shadow-2xl border border-border/60 overflow-hidden"
        style={positionStyle}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border-light px-4 py-2">
          <h3 className="text-sm font-semibold text-foreground">
            Assign a task
            {time != null && mediaType !== 'image' && (
              <span className="ml-1 font-normal text-muted-foreground">
                {showDuration
                  ? `${formatMilliSeconds(time)}–${formatMilliSeconds(time + duration * 1000)}`
                  : `(${formatMilliSeconds(time)})`}
              </span>
            )}
          </h3>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-accent-foreground">
            <X className="size-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col">
          {/* Line type selector + duration (for arrows) */}
          {showLineOptions && (
            <div className="flex items-center gap-2 border-b border-border-light px-4 py-2">
              <label className="text-xs text-muted-foreground">Type:</label>
              <div className="flex gap-1">
                {LINE_TYPE_OPTIONS.map(({ value, icon: Icon, label }) => (
                  <button
                    key={value}
                    type="button"
                    title={label}
                    className={`rounded p-1.5 ${taskType === value ? 'bg-info-light text-info' : 'text-muted-foreground hover:bg-secondary'}`}
                    onClick={() => setTaskType(value)}
                  >
                    <Icon className="size-4" />
                  </button>
                ))}
              </div>
              {showDuration && (
                <>
                  <label className="ml-2 text-xs text-muted-foreground">Duration:</label>
                  <input
                    type="number"
                    min={0.5}
                    max={100}
                    step={0.5}
                    value={duration}
                    onChange={(e) => handleDurationChange(parseFloat(e.target.value) || 0.5)}
                    className="w-14 rounded border border-border px-2 py-0.5 text-xs"
                  />
                  <span className="text-xs text-muted-foreground">s</span>
                </>
              )}
            </div>
          )}

          {/* Message textarea */}
          <div className="p-3">
            <textarea
              className="w-full resize-none rounded border border-border p-2 text-sm outline-none placeholder:text-muted-foreground focus:border-info"
              placeholder="Write something"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              autoFocus
            />
          </div>

          {/* Submit */}
          <div className="flex justify-end border-t border-border-light px-3 py-2">
            <Button type="submit" size="sm" disabled={isSubmitting || !message.trim()}>
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
